// =============================================================
// RecommendationsPanel.jsx — opportunities the AI screen graded at or above
// the bar, one row per (opportunity, company), with Accept / Reject.
//
// The grade comes from the notice's description only. A low-confidence grade
// within 20 points of the bar is shown too, flagged, so a thin description does
// not hide a bid behind a guess. Every number here is the server's; the client
// derives nothing.
// =============================================================
import { useState, useEffect, useCallback } from 'react';
import { B } from '../../constants/brand';
import { fmt } from '../../utils/format';
import { api } from '../../api/client';
import { Btn, Input, Card, Badge, Spinner, EmptyState, Tabs, useToast } from '../ui';

const STATUS_TABS = { 'To review': 'open', Accepted: 'accepted', Rejected: 'rejected', All: 'all' };

const gradeColor = (g) => (g >= 80 ? B.green : g >= 60 ? B.tealDk : B.textMd);

export function RecommendationsPanel({ onOpenDocs }) {
  const { toast } = useToast();
  const [tab, setTab] = useState('To review');
  const [status, setStatus] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [running, setRunning] = useState(false);
  const [threshold, setThreshold] = useState('');

  const loadStatus = useCallback(async () => {
    try {
      const s = await api.get('govbid/status');
      setStatus(s);
      if (s?.threshold != null) setThreshold(String(s.threshold));
    } catch (e) { setStatus({ available: false, reason: e.message }); }
  }, []);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try { setData(await api.get(`govbid/recommendations?status=${STATUS_TABS[tab]}`)); }
    catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [tab]);

  useEffect(() => { loadStatus(); }, [loadStatus]);
  useEffect(() => { load(); }, [load]);

  const runNow = async () => {
    setRunning(true);
    try {
      const out = await api.post('govbid/screen/run', { limit: 20 }, { timeout: 60000 });
      if (out.reason && !out.considered) toast.info(out.reason);
      else toast.success(`Screened ${out.considered}: ${out.graded} graded, ${out.ineligible} no company eligible${out.failed ? `, ${out.failed} failed` : ''}.`);
      await Promise.all([loadStatus(), load()]);
    } catch (e) { toast.error(e.message); }
    finally { setRunning(false); }
  };

  const saveThreshold = async () => {
    try {
      const s = await api.put('govbid/settings', { threshold: Number(threshold) });
      toast.success(`Bar set to ${s.threshold}.`);
      await Promise.all([loadStatus(), load()]);
    } catch (e) { toast.error(e.message); }
  };

  const toggleEnabled = async () => {
    try { await api.put('govbid/settings', { enabled: !status.enabled }); await loadStatus(); }
    catch (e) { toast.error(e.message); }
  };

  const decide = async (item, decision) => {
    let note = null;
    if (decision === 'rejected') {
      note = window.prompt('Why not bid? (optional — this is what tunes the screen)', '') ;
      if (note === null) return;
    }
    try {
      await api.post(`govbid/grades/${item.gradeId}/decision`, { decision, note });
      setData(d => ({
        ...d,
        items: d.items
          .map(i => (i.gradeId === item.gradeId ? { ...i, decision, decisionNote: note } : i))
          .filter(i => STATUS_TABS[tab] === 'all' || (STATUS_TABS[tab] === 'open' ? !i.decision : i.decision === STATUS_TABS[tab])),
      }));
      loadStatus();
    } catch (e) { toast.error(e.message); }
  };

  if (status && status.available === false) {
    return <Card><div style={{ fontSize: 13, color: B.textMd }}>{status.reason}</div></Card>;
  }

  return (
    <div>
      {status && (
        <Card style={{ marginBottom: 16, display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 16 }}>
          <div style={{ flex: 1, minWidth: 260, fontSize: 13, color: B.textMd, lineHeight: 1.6 }}>
            <div><b style={{ color: B.text }}>{status.biddingCompanies}</b> bidding compan{status.biddingCompanies === 1 ? 'y' : 'ies'} ·{' '}
              <b style={{ color: B.text }}>{status.awaitingDecision}</b> to review ·{' '}
              <b style={{ color: B.text }}>{status.waiting}</b> waiting to be screened</div>
            <div>{status.graded} graded · {status.ineligible} with no eligible company{status.failed ? ` · ${status.failed} failed (retried after 24 h)` : ''}</div>
            {status.biddingCompanies === 0 && <div style={{ color: B.amber }}>Nothing is screened until a company has a bid profile — see the Bid profiles tab.</div>}
            {!status.aiReady && <div style={{ color: B.red }}>No AI connection is set for “Gov Opportunity Bid Screen” (Settings → AI Connections). The rules still run.</div>}
            {status.lastRun?.finishedUTC && <div>Last screen {fmt.date(status.lastRun.finishedUTC)}. Runs hourly{status.enabled ? '' : ' — paused'}.</div>}
          </div>
          <div style={{ width: 120 }}>
            <Input label="Bar (0-100)" value={threshold} onChange={setThreshold} />
          </div>
          <Btn variant="ghost" onClick={saveThreshold} disabled={String(status.threshold) === threshold}>Set bar</Btn>
          <Btn variant="ghost" onClick={toggleEnabled}>{status.enabled ? 'Pause hourly screen' : 'Resume hourly screen'}</Btn>
          <Btn onClick={runNow} loading={running} disabled={running}>Screen next 20 now</Btn>
        </Card>
      )}

      <Tabs tabs={Object.keys(STATUS_TABS)} active={tab} onChange={setTab} />

      {loading ? <div style={{ padding: 40, textAlign: 'center' }}><Spinner /></div>
        : error ? <Card><div style={{ color: B.red, fontSize: 13 }}>{error}</div></Card>
        : !data?.items?.length ? (
          <EmptyState title="Nothing here" message={tab === 'To review'
            ? 'No screened opportunity has cleared the bar yet.'
            : 'No opportunities in this list.'} />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {data.items.map(item => <RecommendationRow key={item.gradeId} item={item} onDecide={decide} onOpenDocs={onOpenDocs} />)}
            {data.truncated && <div style={{ fontSize: 12, color: B.textLt }}>Showing the top 200.</div>}
          </div>
        )}
    </div>
  );
}

function RecommendationRow({ item, onDecide, onOpenDocs }) {
  const o = item.opportunity;
  return (
    <Card style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
      <div style={{ minWidth: 56, textAlign: 'center' }}>
        <div style={{ fontSize: 24, fontWeight: 700, color: gradeColor(item.grade), lineHeight: 1 }}>{item.grade}</div>
        <div style={{ fontSize: 10, color: B.textLt, marginTop: 2 }}>of 100</div>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', marginBottom: 4 }}>
          <Badge color={B.navy}>{item.entityName || item.entityCode}</Badge>
          {item.confidence === 'low' && <Badge color={B.amber}>Low confidence{item.belowThreshold ? ' · below the bar' : ''}</Badge>}
          {item.decision === 'accepted' && <Badge color={B.green}>Accepted</Badge>}
          {item.decision === 'rejected' && <Badge color={B.red}>Rejected</Badge>}
        </div>
        <div style={{ fontWeight: 600, color: B.text, fontSize: 14 }}>
          {o.uiLink ? <a href={o.uiLink} target="_blank" rel="noreferrer" style={{ color: B.text }}>{o.title || o.noticeId}</a> : (o.title || o.noticeId)}
        </div>
        <div style={{ fontSize: 12, color: B.textMd, marginTop: 2 }}>
          {[o.agency, o.solicitationNumber, o.setAsideType, o.naicsCode && `NAICS ${o.naicsCode}`,
            o.estimatedValue ? fmt.currency(o.estimatedValue) : null,
            o.responseDeadline ? `Due ${fmt.date(o.responseDeadline)}` : 'No deadline stated',
          ].filter(Boolean).join(' · ')}
        </div>
        {item.summary || item.pitch ? (
          <>
            {item.summary && <div style={{ fontSize: 13, color: B.text, marginTop: 8, lineHeight: 1.5 }}>{item.summary}</div>}
            {item.pitch && <div style={{ fontSize: 13, color: B.navy, fontWeight: 600, marginTop: 6, lineHeight: 1.5 }}>{item.pitch}</div>}
          </>
        ) : (
          <div style={{ fontSize: 12, color: B.textLt, marginTop: 8 }}>Summary not written yet — it is added the next time this opportunity is screened.</div>
        )}
        {item.notes?.length > 0 && <div style={{ fontSize: 12, color: B.textLt, marginTop: 4 }}>Not on the notice: {item.notes.join(' ')}</div>}
        {item.decisionNote && <div style={{ fontSize: 12, color: B.textMd, marginTop: 4 }}>Your note: {item.decisionNote}</div>}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {item.decision !== 'accepted' && <Btn size="sm" onClick={() => onDecide(item, 'accepted')}>Accept</Btn>}
        {item.decision !== 'rejected' && <Btn size="sm" variant="ghost" onClick={() => onDecide(item, 'rejected')}>Reject</Btn>}
        {item.decision && <Btn size="sm" variant="ghost" onClick={() => onDecide(item, null)}>Undo</Btn>}
        {onOpenDocs && <Btn size="sm" variant="ghost" onClick={() => onOpenDocs(o)}>Documents</Btn>}
      </div>
    </Card>
  );
}
